import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  WarningAmberOutlined,
  CheckCircleOutline,
} from "@mui/icons-material";

type ReasonConfig = {
  label?: string;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
};

type ConfirmationModalProps = {
  show: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  title?: string;
  message?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  isLoading?: boolean;
  variant?: "default" | "danger" | "warning" | "success";
  requiresConfirmation?: boolean;
  confirmationText?: string;
  contextName?: string;
  reasonConfig?: ReasonConfig;
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  show,
  onClose,
  onConfirm,
  title = "Are you sure?",
  message = "Confirm this action.",
  confirmButtonText = "Confirm",
  cancelButtonText = "Cancel",
  isLoading = false,
  variant = "default",
  requiresConfirmation = false,
  confirmationText = "CONFIRM",
  contextName,
  reasonConfig,
}) => {
  const [confirmationInput, setConfirmationInput] = useState("");
  const [shake, setShake] = useState(false);
  const [reasonInput, setReasonInput] = useState("");

  // Animation states
  const [rendered, setRendered] = useState(false);
  const [animate, setAnimate] = useState(false);

  const requiresReason = !!reasonConfig;

  useEffect(() => {
    if (show) {
      setRendered(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimate(true));
      });
    } else {
      setAnimate(false);
      const t = setTimeout(() => setRendered(false), 300);
      return () => clearTimeout(t);
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      setConfirmationInput("");
      setReasonInput("");
      setShake(false);
      // Prevent background scroll while modal is open
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  const handleConfirm = () => {
    if (requiresConfirmation && confirmationInput !== confirmationText) {
      setShake(true);
      setTimeout(() => setShake(false), 400);
      return;
    }
    if (requiresReason && !reasonInput.trim()) {
      setShake(true);
      setTimeout(() => setShake(false), 400);
      return;
    }
    onConfirm(reasonInput || undefined);
  };

  const trimmed = reasonInput.trim();
  const reasonTooShort =
    !!reasonConfig?.minLength && trimmed.length < reasonConfig.minLength;
  const reasonTooLong =
    !!reasonConfig?.maxLength && trimmed.length > reasonConfig.maxLength;

  const isDisabled = Boolean(
    isLoading ||
    (requiresConfirmation && confirmationInput !== confirmationText) ||
    (requiresReason && !trimmed) ||
    (requiresReason && reasonTooShort) ||
    (requiresReason && reasonTooLong)
  );

  const variantMap = {
    default: {
      icon: <WarningAmberOutlined className="text-[#D4A354] text-4xl" />,
      confirmButton: "bg-[#D4A354] hover:bg-[#C19540] focus:ring-[#D4A354]",
      iconBg: "bg-[#FEF7E6] border border-[#E5C470]",
      titleColor: "text-[#B8832A]",
    },
    danger: {
      icon: <WarningAmberOutlined className="text-red-600 text-4xl" />,
      confirmButton: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
      iconBg: "bg-red-50 border border-red-200",
      titleColor: "text-red-700",
    },
    warning: {
      icon: <WarningAmberOutlined className="text-amber-600 text-4xl" />,
      confirmButton: "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500",
      iconBg: "bg-amber-50 border border-amber-200",
      titleColor: "text-amber-700",
    },
    success: {
      icon: <CheckCircleOutline className="text-green-600 text-4xl" />,
      confirmButton: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
      iconBg: "bg-green-50 border border-green-200",
      titleColor: "text-green-700",
    },
  };

  const styles = variantMap[variant];

  if (!rendered) return null;

  // createPortal renders directly into document.body,
  // escaping any parent transform/filter/will-change that breaks fixed positioning
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        // onClick={onClose}
        style={{ transition: "opacity 300ms ease" }}
        className={`fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm ${animate ? "opacity-100" : "opacity-0"
          }`}
      />

      {/* Centering wrapper */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
        style={{ pointerEvents: "none" }}
      >
        <div
          style={{
            pointerEvents: "auto",
            transition:
              "opacity 300ms ease, transform 300ms cubic-bezier(0.34,1.3,0.64,1)",
          }}
          className={`w-full max-w-md ${animate
            ? "opacity-100 translate-y-0 scale-100"
            : "opacity-0 translate-y-5 scale-95"
            }`}
        >
          {/* Modal card */}
          <div className="bg-white rounded-2xl select-none shadow-2xl p-8 relative">
            {/* Icon */}
            <div
              className={`mx-auto w-16 h-16 ${styles.iconBg} rounded-full flex items-center justify-center mb-4 animate-bounce`}
            >
              {styles.icon}
            </div>

            {/* Title */}
            <h2 className={`text-xl font-bold text-center ${styles.titleColor}`}>
              {title}
            </h2>

            {/* Message */}
            <p className="text-center text-gray-600 mt-3 leading-relaxed">
              {message}
              {contextName && (
                <span className="font-semibold text-gray-800 block mt-1">
                  "{contextName}"
                </span>
              )}
            </p>

            {/* Confirmation Input */}
            {requiresConfirmation && (
              <div className={`mt-5 ${shake ? "animate-shake" : ""}`}>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Type{" "}
                  <strong className="text-[#D4A354]">{confirmationText}</strong>{" "}
                  to confirm
                </label>
                <input
                  value={confirmationInput}
                  onChange={(e) => setConfirmationInput(e.target.value)}
                  placeholder={`Type "${confirmationText}"`}
                  className="select-text w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4A354] focus:border-[#D4A354]"
                />
              </div>
            )}

            {/* Reason Input */}
            {reasonConfig && (
              <div className={`mt-5 ${shake ? "animate-shake" : ""}`}>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  {reasonConfig.label ?? "Reason"}
                </label>
                <textarea
                  value={reasonInput}
                  onChange={(e) => setReasonInput(e.target.value)}
                  placeholder={reasonConfig.placeholder ?? "Enter reason..."}
                  rows={3}
                  className="select-text w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4A354] focus:border-[#D4A354] resize-none"
                />
                {reasonConfig.minLength &&
                  trimmed.length > 0 &&
                  reasonTooShort && (
                    <p className="text-red-500 text-xs mt-1">
                      Minimum {reasonConfig.minLength} characters required
                    </p>
                  )}
                {reasonConfig.maxLength && reasonTooLong && (
                  <p className="text-red-500 text-xs mt-1">
                    Maximum {reasonConfig.maxLength} characters allowed
                  </p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-center gap-4 mt-8">
              <button
                onClick={onClose}
                className="px-8 py-3 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
              >
                {cancelButtonText}
              </button>

              <button
                onClick={handleConfirm}
                disabled={isDisabled}
                className={`px-8 py-3 text-white rounded-lg transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDisabled
                  ? "bg-gray-400 cursor-not-allowed opacity-60"
                  : styles.confirmButton
                  }`}
              >
                {isLoading ? "Processing..." : confirmButtonText}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Shake animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25%       { transform: translateX(-4px); }
          50%       { transform: translateX(4px); }
          75%       { transform: translateX(-4px); }
        }
        .animate-shake { animation: shake 0.3s; }
      `}</style>
    </>,
    document.body
  );
};

export default ConfirmationModal;